/**
 * Verificar2FA — OTP gate after login when user has 2FA enabled.
 *
 * Input: location.state.challenge_token, .email_masked
 *   OR  sessionStorage backup (loginTwoFaPending)
 *
 * Submits { challenge_token, codigo } to /auth/2fa/verify-login to exchange for
 * a real access_token.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, CheckCircle2, Loader2, RotateCcw, AlertCircle } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Button } from '../components/ui/button';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

const RESEND_SECONDS = 60;
const STORAGE_KEY = 'loginTwoFaPending';

export default function Verificar2FA() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin } = useAuth();

  const initial = (() => {
    const fromState = location?.state;
    if (fromState?.challenge_token) return fromState;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  })();

  const [challengeToken] = useState(initial?.challenge_token || '');
  const [emailMasked] = useState(initial?.email_masked || '');
  const [phoneMasked] = useState(initial?.phone_masked || '');
  const [channel] = useState(initial?.channel || 'email');
  const [email] = useState(initial?.email || '');
  const [password] = useState(initial?.password || ''); // used to re-trigger resend
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_SECONDS);
  const [error, setError] = useState('');
  const cooldownRef = useRef(null);

  useEffect(() => {
    if (!challengeToken) {
      toast.error('Sessão de verificação inválida. Faça login novamente.');
      navigate('/login', { replace: true });
    } else {
      // Persist so reload doesn't lose the challenge
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ challenge_token: challengeToken, email_masked: emailMasked, phone_masked: phoneMasked, channel, email, password }));
      } catch (_) { /* ignore */ }
    }
  }, [challengeToken, emailMasked, phoneMasked, channel, email, password, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    cooldownRef.current = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(cooldownRef.current);
  }, [cooldown]);

  const handleVerify = async (codigoOverride) => {
    const codigo = (codigoOverride ?? otp).trim();
    if (codigo.length !== 6) {
      setError('Digite os 6 dígitos do código.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post('/auth/2fa/verify-login', { challenge_token: challengeToken, codigo });
      sessionStorage.removeItem(STORAGE_KEY);
      if (data?.access_token) {
        authLogin(data.access_token, data.user, data.refresh_token || null);
      }
      toast.success('Bem-vindo de volta!');
      navigate('/', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Código inválido ou expirado';
      setError(detail);
      setOtp('');
      if (err?.response?.status === 401) {
        // Challenge expired — send them back to login
        setTimeout(() => {
          sessionStorage.removeItem(STORAGE_KEY);
          navigate('/login', { replace: true });
        }, 1800);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending || !email || !password) {
      toast.error('Para reenviar o código, faça login novamente.');
      return;
    }
    setResending(true);
    try {
      // A fresh login call will re-send the 2FA code
      await api.post('/auth/login', { email, password });
      toast.success('Código reenviado.');
      setCooldown(RESEND_SECONDS);
    } catch (_) {
      toast.error('Não foi possível reenviar. Faça login novamente.');
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (otp.length === 6 && !submitting) handleVerify(otp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-pink-50 px-4 py-12" data-testid="verificar-2fa-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 sm:p-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
            <ShieldCheck className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Verificação em duas etapas
          </h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            {channel === 'sms' ? (
              <>Enviamos um SMS com o código de acesso para{' '}
                <strong className="text-gray-900" data-testid="two-fa-phone-masked">{phoneMasked || 'seu telefone'}</strong>.
              </>
            ) : (
              <>Enviamos um código de acesso para{' '}
                <strong className="text-gray-900" data-testid="two-fa-email-masked">{emailMasked || 'seu e-mail'}</strong>.
              </>
            )}
          </p>
        </div>

        <div className="flex flex-col items-center gap-3">
          <InputOTP
            maxLength={6}
            value={otp}
            onChange={(v) => { setOtp(v); if (error) setError(''); }}
            disabled={submitting}
            data-testid="two-fa-otp-input"
            autoFocus
            inputMode="numeric"
          >
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot key={i} index={i} className="w-11 h-12 sm:w-12 sm:h-14 text-lg" />
              ))}
            </InputOTPGroup>
          </InputOTP>

          {error && (
            <p className="text-sm text-red-600 flex items-center gap-1.5 mt-1" data-testid="two-fa-error">
              <AlertCircle className="w-4 h-4" /> {error}
            </p>
          )}
        </div>

        <Button
          className="w-full mt-6 bg-purple-600 hover:bg-purple-700 h-11"
          onClick={() => handleVerify()}
          disabled={submitting || otp.length !== 6}
          data-testid="two-fa-submit-btn"
        >
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verificando...</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Entrar</>}
        </Button>

        <div className="mt-6 text-center text-sm text-gray-600">
          Não recebeu o código?{' '}
          {cooldown > 0 ? (
            <span className="text-gray-400" data-testid="two-fa-countdown">Reenviar em {cooldown}s</span>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="text-purple-600 hover:text-purple-700 font-semibold disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="two-fa-resend-btn"
            >
              {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              Reenviar código
            </button>
          )}
        </div>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => { sessionStorage.removeItem(STORAGE_KEY); navigate('/login'); }}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Voltar ao login
          </button>
        </div>
      </div>
    </div>
  );
}
